import {
  appFormUtil,
  gqlCacheUtil
} from 'modules/common';
import React, { useEffect } from 'react';
import { DateTime } from 'luxon';
import {
  useSaveOnSiteWorkEventMutation,
  SaveOnSiteWorkEvent_Input,
  DailyReportDetailSectionQuery,
  SaveOnSiteWorkEventMutation,
  DailyReportDetailSectionDocument,
  useOnSiteWorkEventFormQuery
} from 'generated/graphql';
import _ from 'lodash';

export type IFormState = {
  id: string;
  dateWorked: string;
  hoursWorked: string;
  projectId: string;
  tradeId: string | null;
  onSiteSubContractors: string;
  description: string;

  personId: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  isOrganization: boolean;
  createPerson: boolean;
};

const initialState: IFormState = {
  dateWorked: DateTime.now().toFormat('yyyy-LL-dd'),
  hoursWorked: '',
  id: '',
  personId: '',
  projectId: '',
  tradeId: '',
  onSiteSubContractors: '0',
  description: '',
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  isOrganization: true,
  createPerson: false
};

export function useFormState(id: string, projectId: string) {
  const { formState, dispatch, genFieldProps } = appFormUtil.useFormState({
    initialState,
    key2FieldConfig: {
      dateWorked: {
        label: 'Date worked',
        type: 'date'
      },
      hoursWorked: {
        label: 'Hours worked',
        type: 'number'
      },
      onSiteSubContractors: {
        label: 'Number of onsite workers',
        type: 'number'
      },
      tradeId: {
        label: 'Trade',
        type: 'text'
      },
      description: {
        label: 'Work performed',
        type: 'text'
      },
      id: {
        label: 'Id',
        type: 'text'
      },
      personId: {
        label: 'Contractor',
        type: 'text'
      },
      projectId: {
        label: 'Project id',
        type: 'text'
      },
      firstName: {
        label: 'First name',
        type: 'text'
      },
      lastName: {
        label: 'Last name',
        type: 'text'
      },
      email: {
        label: 'Email',
        type: 'email'
      },
      phone: {
        label: 'Phone',
        type: 'tel'
      },
      isOrganization: {
        label: 'isOrganization',
        type: 'text'
      },
      createPerson: {
        label: '',
        type: 'text'
      }
    }
  });

  useEffect(() => {
    if (formState.projectId === '' && projectId !== '') {
      dispatch({
        type: 'UPDATE',
        payload: {
          property: 'projectId',
          value: projectId
        }
      });
    }
    return () => { };
  }, [
    dispatch,
    formState.projectId,
    projectId
  ]);

  const { data, loading, called } = useQuery(id);

  useEffect(() => {
    if (formState.id === '' && data.id !== '' && !loading && called) {
      dispatch({
        type: 'BATCH_UPDATE',
        payload: {
          ...formState,
          ...data
        }
      });
    }
  }, [
    called,
    data,
    dispatch,
    formState,
    loading
  ]);

  return React.useMemo(
    () => {

      return {
        formState,
        dispatch,
        genFieldProps
      };
    },
    [
      dispatch,
      formState,
      genFieldProps
    ]
  );
}


export interface IData extends Omit<IFormState, 'isOrganization' | 'firstName' | 'lastName' | 'email' | 'phone' | 'createPerson'> { }

export function useQuery(id: string) {
  const tuple = useOnSiteWorkEventFormQuery({
    variables: {
      id
    },
    skip: id === ''
  });

  return React.useMemo(
    () => {
      const data: IData = {
        id: tuple.data?.onSiteWorkEvent?.id ?? '',
        dateWorked: tuple.data?.onSiteWorkEvent?.dateWorked.toFormat('yyyy-LL-dd') ?? '',
        hoursWorked: _.toString(tuple.data?.onSiteWorkEvent?.hoursWorked) ?? '0',
        personId: tuple.data?.onSiteWorkEvent?.person.id ?? '',
        projectId: tuple.data?.onSiteWorkEvent?.project?.id ?? '',
        onSiteSubContractors: _.toString(tuple.data?.onSiteWorkEvent?.onSiteSubContractors) ?? '0',
        description: tuple.data?.onSiteWorkEvent?.description ?? '',
        tradeId: tuple.data?.onSiteWorkEvent?.trade?.id ?? null
      };

      return {
        ...tuple,
        data
      };
    },
    [tuple]
  );
}

export function useSaveOnSiteWorkEvent() {
  const [
    fnMutate,
    tuple
  ] = useSaveOnSiteWorkEventMutation({
    refetchQueries: []
  });
  const fnWrappedMutate = React.useCallback(
    async (input: SaveOnSiteWorkEvent_Input) => {
      await fnMutate({
        variables: {
          input
        },
        updateQueries: {
          ...gqlCacheUtil.genUpdateQueryMap<DailyReportDetailSectionQuery, SaveOnSiteWorkEventMutation>(
            DailyReportDetailSectionDocument, (prevState, { mutationResult }) => {
              if (!prevState.dailyReport?.workLog) {
                return prevState;
              }

              if (!mutationResult.data?.saveOnSiteWorkEvent) {
                return prevState;
              }

              const isNew = input.id !== mutationResult.data.saveOnSiteWorkEvent.id;

              if (isNew) {
                return {
                  ...prevState,
                  dailyReport: {
                    ...prevState.dailyReport,
                    workLog: _.compact([
                      ...prevState.dailyReport.workLog,
                      mutationResult.data.saveOnSiteWorkEvent
                    ])
                  }
                };
              } else {
                const updatedWorkLog = prevState.dailyReport.workLog.map((node) => {
                  if (node.id === mutationResult.data?.saveOnSiteWorkEvent?.id) {
                    return {
                      ...node,
                      ...mutationResult.data.saveOnSiteWorkEvent
                    };
                  }
                  return node;
                });

                return {
                  ...prevState,
                  dailyReport: {
                    ...prevState.dailyReport,
                    workLog: _.compact(updatedWorkLog)
                  }
                };
              }


              return prevState;

            }
          )
        }
      });
    },
    [fnMutate]
  );

  return React.useMemo(
    () => [
      fnWrappedMutate,
      {
        ...tuple,
        data: null
      }
    ] as const,
    [
      fnWrappedMutate,
      tuple
    ]
  );
}
